"use client";

type Props = {
  radiusKm: number;
  onRadiusChange: (km: number) => void;
  startDays?: number;
  endDays?: number;
  onStartDaysChange?: (days: number) => void;
  onEndDaysChange?: (days: number) => void;
  showDateRange?: boolean;
  maxDays?: number; // Massimo numero di giorni per lo slider (default: 30)
};

// Funzione per formattare la data con il giorno della settimana
function formatDateWithWeekday(days: number): string {
  const date = new Date(Date.now() + days * 86400000);
  const weekday = date.toLocaleDateString("it-IT", { weekday: "short" });
  const day = date.toLocaleDateString("it-IT", { day: "numeric" });
  const month = date.toLocaleDateString("it-IT", { month: "short" });
  return `${weekday} ${day} ${month}`;
}

export default function MapControls({
  radiusKm,
  onRadiusChange,
  startDays,
  endDays,
  onStartDaysChange,
  onEndDaysChange,
  showDateRange = false,
  maxDays = 30,
}: Props) {
  return (
    <div className="radius-selector mb-6" style={{ marginBottom: "var(--sp-6)" }}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "1rem",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: ".5rem",
            minWidth: 280,
            flex: 1,
            maxWidth: 560,
          }}
        >
          <label className="radius-selector__label" htmlFor="radius-select">
            Raggio di ricerca
          </label>
          <div
            className="radius-selector__options"
            role="radiogroup"
            aria-labelledby="radius-label"
          >
            {[10, 25, 50, 100].map((km) => (
              <button
                key={km}
                type="button"
                role="radio"
                aria-checked={radiusKm === km}
                className={`radius-selector__option ${radiusKm === km ? "radius-selector__option--active" : ""}`}
                onClick={() => onRadiusChange(km)}
                aria-label={`Cerca sagre entro ${km} chilometri`}
              >
                {km} km
              </button>
            ))}
          </div>
        </div>

        {/* Intervallo date (solo se abilitato) */}
        {showDateRange && startDays !== undefined && endDays !== undefined && onStartDaysChange && onEndDaysChange && (
          <div
            style={{ minWidth: 280, flex: 1, maxWidth: 560 }}
            aria-label="Filtro intervallo date"
          >
            <label
              className="radius-selector__label"
              htmlFor="horizon-start"
              style={{ display: "block", marginBottom: 0 }}
            >
              Intervallo date
            </label>
            <div>
              {/* Slider inizio */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, width: 130 }}>
                  <span className="text-xs text-stone-600">Inizio:</span>
                  <span
                    className="text-xs text-stone-700 font-bold"
                    style={{ fontWeight: "bold" }}
                  >
                    {formatDateWithWeekday(startDays)}
                  </span>
                </div>
                <input
                  id="horizon-start"
                  type="range"
                  min={0}
                  max={maxDays}
                  value={startDays}
                  onChange={(e) => {
                    const v = Math.min(parseInt(e.target.value, 10), endDays);
                    onStartDaysChange(v);
                  }}
                  style={{ flex: 1, minWidth: 0, accentColor: "var(--color-primary)" }}
                  aria-label="Giorno di inizio"
                />
              </div>

              {/* Slider fine */}
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, width: 130 }}>
                  <span className="text-xs text-stone-600">Fine:</span>
                  <span
                    className="text-xs text-stone-700 font-bold"
                    style={{ fontWeight: "bold" }}
                  >
                    {formatDateWithWeekday(endDays)}
                  </span>
                </div>
                <input
                  id="horizon-end"
                  type="range"
                  min={0}
                  max={maxDays}
                  value={endDays}
                  onChange={(e) => {
                    const v = Math.max(parseInt(e.target.value, 10), startDays);
                    onEndDaysChange(v);
                  }}
                  style={{ flex: 1, minWidth: 0, accentColor: "var(--color-primary)" }}
                  aria-label="Giorno di fine"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

